import React, { useState, useMemo } from 'react';
import {
    Box,
    Paper,
    TablePagination,
    TableSortLabel,
    Typography,
    Avatar,
    IconButton,
    Chip,
    Tooltip,
    CircularProgress,
    Button,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
} from '@mui/material';
import {
    MoreVertIcon,
    EditOutlinedIcon,
    DeleteOutlineIcon,
    PersonOutlineOutlinedIcon,
    PersonIcon
} from 'icons/materialIcons';
import PropTypes from 'prop-types';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';
import { useEmailSignatureContext } from 'contexts/EmailSignatureContext';
import TeamEmailSignatureAssignMembersModal from './TeamEmailSignatureAssignMembersModal';
import TeamEmailSignatureContent from 'team/components/editMember/emailSignature/TeamEmailSignatureContent';

// Mini preview component for table thumbnail - uses actual email signature preview component
// Uses the same card data as the preview but applies template's visibility settings
const EmailSignaturePreviewThumbnail = ({ template, defaultCardId, emailSignatureState: cardEmailSignatureState }) => {
    // Use the same card data as the preview, but apply template's visibility settings
    const previewState = useMemo(() => {
        if (!template || !defaultCardId || !cardEmailSignatureState?.[defaultCardId]) {
            return {};
        }
        
        // Get the card data (same as preview uses)
        const cardData = cardEmailSignatureState[defaultCardId];
        
        // Use card data but apply template's visibility settings and overrides
        return {
            [defaultCardId]: {
                // Use card data for content
                emailSignatureName: template.emailSignatureName || cardData.emailSignatureName || '',
                emailSignatureJobTitle: template.emailSignatureJobTitle || cardData.emailSignatureJobTitle || '',
                emailSignatureCompany: template.emailSignatureCompany || cardData.emailSignatureCompany || '',
                emailSignatureLocation: template.emailSignatureLocation || cardData.emailSignatureLocation || '',
                emailSignaturePhoneNumber: template.emailSignaturePhoneNumber || cardData.emailSignaturePhoneNumber || '',
                emailSignatureProfilePictureUrl: template.emailSignatureProfilePictureUrl || cardData.emailSignatureProfilePictureUrl || '',
                emailSignatureCompanyLogoUrl: template.emailSignatureCompanyLogoUrl || cardData.emailSignatureCompanyLogoUrl || '',
                emailSignatureBannerImageUrl: template.emailSignatureBannerImageUrl || cardData.emailSignatureBannerImageUrl || '',
                emailSignatureDisclaimer: template.emailSignatureDisclaimer || cardData.emailSignatureDisclaimer || '',
                // Apply template's visibility settings
                emailSignatureShowName: template.emailSignatureShowName !== false,
                emailSignatureShowPronouns: template.emailSignatureShowPronouns || false,
                emailSignatureShowJobTitle: template.emailSignatureShowJobTitle !== false,
                emailSignatureShowCompany: template.emailSignatureShowCompany !== false,
                emailSignatureShowLocation: template.emailSignatureShowLocation !== false,
                emailSignatureShowPhoneNumber: template.emailSignatureShowPhoneNumber !== false,
                emailSignatureShowProfilePic: template.emailSignatureShowProfilePic !== false,
                emailSignatureShowCompanyLogo: template.emailSignatureShowCompanyLogo !== false,
                emailSignatureIncludeQRCode: template.emailSignatureIncludeQRCode !== false,
                emailSignatureIncludeBannerImage: template.emailSignatureIncludeBannerImage || false,
                emailSignatureLinkTypes: template.emailSignatureLinkTypes || cardData.emailSignatureLinkTypes || []
            }
        };
    }, [template, defaultCardId, cardEmailSignatureState]);

    if (!template) {
        return (
            <Box sx={{ 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#999',
                fontSize: '8px'
            }}>
                No preview
            </Box>
        );
    }

    return (
        <Box sx={{ 
            width: '100%', 
            height: '100%', 
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <Box sx={{ 
                transform: 'scale(0.18)', 
                transformOrigin: 'top left', 
                width: '600px', 
                height: '400px',
                position: 'absolute',
                top: 0,
                left: 0,
                pointerEvents: 'none'
            }}>
                {defaultCardId && previewState[defaultCardId] ? (
                    <TeamEmailSignatureContent
                        emailSignatureState={previewState}
                        cardId={defaultCardId}
                        setLoading={() => {}}
                        setError={() => {}}
                    />
                ) : (
                    <Box sx={{ 
                        width: '100%', 
                        height: '100%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: '#999',
                        fontSize: '12px',
                        padding: 2
                    }}>
                        Loading preview...
                    </Box>
                )}
            </Box>
        </Box>
    );
};

EmailSignaturePreviewThumbnail.propTypes = {
    template: PropTypes.object,
    defaultCardId: PropTypes.string,
    emailSignatureState: PropTypes.object
};

const TeamEmailSignatureTable = ({
    templates = [],
    isLoading = false,
    onEdit,
    onDelete,
    onAssignMembers,
    teamId,
    defaultCardId,
    emailSignatureState
}) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('date');
    const [order, setOrder] = useState('desc');
    const [hoveredRow, setHoveredRow] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [assignMembersModalOpen, setAssignMembersModalOpen] = useState(false);
    const [selectedTemplateForAssign, setSelectedTemplateForAssign] = useState(null);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatShortDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = () => {
        const isAsc = orderBy === 'date' && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy('date');
    };

    const handleMenuOpen = (event, template) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedTemplate(template);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedTemplate(null);
    };

    const handleEdit = () => {
        if (selectedTemplate && onEdit) {
            onEdit(selectedTemplate);
        }
        handleMenuClose();
    };

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleDeleteConfirm = () => {
        if (selectedTemplate && onDelete) {
            onDelete(selectedTemplate._id);
        }
        setDeleteDialogOpen(false);
        setSelectedTemplate(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSelectedTemplate(null);
    };

    const handleAssignMembers = (template) => {
        setSelectedTemplateForAssign(template);
        setAssignMembersModalOpen(true);
    };

    const handleAssignMembersClose = () => {
        setAssignMembersModalOpen(false);
        setSelectedTemplateForAssign(null);
    };

    const handleAssignMembersSave = async (data) => {
        if (onAssignMembers) {
            await onAssignMembers(data);
        }
        handleAssignMembersClose();
    };

    // Row hover handlers
    const handleRowMouseEnter = (templateId) => {
        setHoveredRow(templateId);
    };

    const handleRowMouseLeave = () => {
        setHoveredRow(null);
    };

    // Loading state
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress size={40} />
                <Typography sx={{ ml: 2, color: '#666' }}>Loading email signatures...</Typography>
            </Box>
        );
    }

    // Empty state
    if (!templates || templates.length === 0) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: 300,
                border: '0.0625rem dashed #e0e0e0',
                borderRadius: '0.75rem',
                backgroundColor: '#fafafa'
            }}>
                <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                    No email signatures found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                    Create your first email signature template by clicking the 'Create Email Signature' button above
                </Typography>
            </Box>
        );
    }

    // Sort templates by date
    const sortedTemplates = [...templates].sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;

        if (order === 'asc') {
            return new Date(a.createdAt) - new Date(b.createdAt);
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
    });

    const emptyRows = page > 0
        ? Math.max(0, (1 + page) * rowsPerPage - templates.length)
        : 0;

    return (
        <Box sx={{ width: '100%', overflow: 'auto' }}>
            {/* Column Headers */}
            <Box sx={{
                width: '100%',
                mb: 0.5,
                background: 'transparent',
                backgroundColor: 'transparent'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    minWidth: '62.5rem',
                    paddingLeft: '0.75rem',
                    paddingRight: '0.75rem',
                    background: 'transparent',
                    backgroundColor: 'transparent',
                    height: '4.5rem'
                }}>
                    <Box sx={{
                        flex: '0 0 25rem',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '1rem 0.75rem',
                        background: 'transparent',
                        backgroundColor: 'transparent',
                        gap: 2
                    }}>
                        {/* Empty space for thumbnail */}
                        <Box sx={{ width: '100px', flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{
                                fontWeight: 600,
                                color: '#666',
                                userSelect: 'none',
                                whiteSpace: 'nowrap',
                                fontSize: '0.875rem',
                                background: 'transparent',
                                backgroundColor: 'transparent'
                            }}>
                                Signature Name
                            </Typography>
                        </Box>
                    </Box>
                    {/* Spacer to push columns to the right */}
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Box sx={{
                        flex: '0 0 10rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1rem 0.5rem',
                        background: 'transparent',
                        backgroundColor: 'transparent'
                    }}>
                        <TableSortLabel
                            active={orderBy === 'date'}
                            direction={order}
                            onClick={handleRequestSort}
                            sx={{
                                color: '#666 !important',
                                '&.Mui-active': { color: '#666 !important' },
                                '& .MuiTableSortLabel-icon': { color: '#666 !important' },
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                background: 'transparent',
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    color: '#666 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{
                                fontWeight: 600,
                                color: '#666',
                                fontSize: '0.875rem',
                                background: 'transparent',
                                backgroundColor: 'transparent',
                                whiteSpace: 'nowrap'
                            }}>
                                Date Created
                            </Typography>
                        </TableSortLabel>
                    </Box>
                    <Box sx={{
                        flex: '0 0 10rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1rem 0.5rem',
                        marginLeft: '2rem',
                        background: 'transparent',
                        backgroundColor: 'transparent'
                    }}>
                        <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: '#666',
                            fontSize: '0.875rem',
                            background: 'transparent',
                            backgroundColor: 'transparent',
                            whiteSpace: 'nowrap'
                        }}>
                            Assigned members
                        </Typography>
                    </Box>
                    <Box sx={{
                        flex: '0 0 10rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1rem 0.5rem',
                        background: 'transparent',
                        backgroundColor: 'transparent'
                    }}>
                        <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: '#666',
                            fontSize: '0.875rem',
                            background: 'transparent',
                            backgroundColor: 'transparent',
                            whiteSpace: 'nowrap'
                        }}>
                            Assigned members
                        </Typography>
                    </Box>
                    <Box sx={{
                        flex: '0 0 8rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1rem 0.5rem',
                        background: 'transparent',
                        backgroundColor: 'transparent'
                    }}>
                        {/* No label - just empty space for the three dots menu */}
                    </Box>
                </Box>
            </Box>

            {/* Table Data - Individual white containers for each template */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {sortedTemplates
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((template) => {
                        if (!template || !template._id) {
                            return null;
                        }

                        const isRowHovered = hoveredRow === template._id;

                        return (
                            <Paper
                                key={template._id}
                                onClick={(e) => {
                                    // Don't trigger edit if clicking on buttons or interactive elements
                                    if (!e.target.closest('button') && !e.target.closest('.MuiIconButton-root') && !e.target.closest('.MuiMenu-root')) {
                                        onEdit && onEdit(template);
                                    }
                                }}
                                onMouseEnter={() => handleRowMouseEnter(template._id)}
                                onMouseLeave={handleRowMouseLeave}
                                sx={{
                                    width: '100%',
                                    backgroundColor: 'white',
                                    borderRadius: '0.75rem',
                                    position: 'relative',
                                    zIndex: 0,
                                    boxShadow: 'none',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: '#f8f9fa',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        minWidth: '62.5rem',
                                        padding: '1rem 0.75rem 1rem 1rem'
                                    }}
                                >
                                    {/* Signature Name with Preview */}
                                    <Box sx={{ flex: '0 0 25rem', display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ 
                                            width: '100px', 
                                            height: '60px', 
                                            borderRadius: '0.5rem',
                                            overflow: 'hidden',
                                            border: '1px solid #e0e0e0',
                                            backgroundColor: '#fafafa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            position: 'relative'
                                        }}>
                                            <EmailSignaturePreviewThumbnail 
                                                template={template}
                                                defaultCardId={defaultCardId}
                                                emailSignatureState={emailSignatureState}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                {template.signatureName || 'Unnamed Signature'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                                Last modified: {formatDate(template.updatedAt || template.createdAt)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Spacer to push columns to the right */}
                                    <Box sx={{ flex: '1 1 auto' }} />

                                    {/* Date Created */}
                                    <Box sx={{ flex: '0 0 10rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                                            {formatShortDate(template.createdAt)}
                                        </Typography>
                                    </Box>

                                    {/* Assigned members */}
                                    <Box sx={{ flex: '0 0 10rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: '2rem' }}>
                                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                                            {(() => {
                                                const count = template?.assignedMembers?.length || 0;
                                                return `${count} member${count === 1 ? '' : 's'}`;
                                            })()}
                                        </Typography>
                                    </Box>

                                    {/* Manage members button */}
                                    <Box sx={{ flex: '0 0 10rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <TeamWhiteButtonWithIcon
                                            icon={PersonOutlineOutlinedIcon}
                                            text="Manage members"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAssignMembers(template);
                                            }}
                                            padding="8px 16px"
                                            iconProps={{ fontSize: '16px' }}
                                        />
                                    </Box>

                                    {/* Actions menu */}
                                    <Box sx={{ flex: '0 0 8rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, template)}
                                            sx={{
                                                color: isRowHovered ? '#666' : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                }
                                            }}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Paper>
                        );
                    })}
            </Box>

            {/* Pagination - Only show if there are more items than default rows per page */}
            {templates.length > rowsPerPage && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={templates.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            '& .MuiTablePagination-toolbar': {
                                paddingLeft: 2,
                                paddingRight: 2
                            }
                        }}
                    />
                </Box>
            )}

            {/* Assign Members Modal */}
            {selectedTemplateForAssign && (
                <TeamEmailSignatureAssignMembersModal
                    open={assignMembersModalOpen}
                    onClose={handleAssignMembersClose}
                    template={(() => {
                        // Get the latest template from the templates list to ensure we have updated assignedMembers
                        if (selectedTemplateForAssign?._id) {
                            const latestTemplate = templates.find(t => t._id?.toString() === selectedTemplateForAssign._id?.toString());
                            return latestTemplate || selectedTemplateForAssign;
                        }
                        return selectedTemplateForAssign;
                    })()}
                    teamId={teamId}
                    onSave={handleAssignMembersSave}
                />
            )}

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleEdit}>
                    <EditOutlinedIcon sx={{ mr: 1, fontSize: 18 }} />
                    Edit
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    <DeleteOutlineIcon sx={{ mr: 1, fontSize: 18 }} />
                    Delete
                </MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete Email Signature Template?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete "{selectedTemplate?.signatureName}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} sx={{ color: '#666' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

TeamEmailSignatureTable.propTypes = {
    templates: PropTypes.arrayOf(PropTypes.object),
    isLoading: PropTypes.bool,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    onAssignMembers: PropTypes.func,
    teamId: PropTypes.string,
    defaultCardId: PropTypes.string,
    emailSignatureState: PropTypes.object
};

export default TeamEmailSignatureTable;

